import React from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
  /** All blocks in the document so headings can be extracted */
  allBlocks?: KBBlock[];
}

interface TocEntry { id: string; level: number; text: string; }

function extractHeadings(blocks: KBBlock[]): TocEntry[] {
  return blocks
    .filter((b) => ['heading_1', 'heading_2', 'heading_3'].includes(b.type))
    .map((b) => ({
      id: b.id,
      level: b.type === 'heading_1' ? 1 : b.type === 'heading_2' ? 2 : 3,
      text: b.content || `Heading ${b.type.slice(-1)}`,
    }));
}

const TableOfContentsBlock: React.FC<Props> = ({ isEditing, onFinish, allBlocks = [] }) => {
  const headings = extractHeadings(allBlocks);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`kb-block-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isEditing) {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />
          </svg>
          <span className="text-sm font-medium text-blue-700">Table of Contents</span>
          <span className="text-xs text-blue-400 ml-1">(auto-generated from headings)</span>
        </div>
        {headings.length > 0 ? (
          <ul className="space-y-1 pl-2">
            {headings.map((h) => (
              <li key={h.id} style={{ paddingLeft: `${(h.level - 1) * 16}px` }}>
                <span className="text-sm text-blue-600">{h.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-blue-400 italic">No headings found in document yet.</p>
        )}
        <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded mt-2">Done</button>
      </div>
    );
  }

  if (headings.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-400 italic">No headings to display.</p>
      </div>
    );
  }

  return (
    <nav className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />
        </svg>
        <span className="text-sm font-semibold text-gray-700">Table of Contents</span>
      </div>
      <ul className="space-y-1.5">
        {headings.map((h) => (
          <li key={h.id} style={{ paddingLeft: `${(h.level - 1) * 16}px` }}>
            <button
              onClick={() => scrollTo(h.id)}
              className={`text-left text-sm text-blue-600 hover:text-blue-800 hover:underline ${h.level === 1 ? 'font-semibold' : h.level === 2 ? 'font-medium' : 'font-normal text-gray-600 hover:text-blue-600'}`}
            >
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContentsBlock;
